/** @jsxImportSource @emotion/react */

import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from "@mui/material";
import BaseButton from "components/common/Button/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import BaseInput from "components/common/Input/Base";
import { ReportType, StoreType, SubType } from "models/business.model";
import {
  getNewzenBusinessList,
  syncNewzenData,
  UploadNewzenData,
  uploadNewzenStore,
} from "pages/remotes";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PALETTE } from "styles/base/variables";
import { convertRegNumber } from "utils/converter";

interface NewzenBusiness {
  code: string;
  name: string;
  period: string;
  regNo: string;
  startDate: string;
  endDate: string;
}

interface ReportDialogProps {
  isOpen: boolean;
  storeType: StoreType | undefined;
  subType?: SubType;
  reportType: ReportType;
  bizId: string | undefined;
  selectedDataIds: string[]; // reportType이 submit일 때만 사용
  onClose: () => void;
  successCallback?: () => void;
}

function ReportDialog({
  isOpen,
  storeType,
  subType,
  reportType,
  bizId,
  selectedDataIds,
  onClose,
  successCallback,
}: ReportDialogProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [spStartDate, spEndDate] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
  ];

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newzenBusinessList, setNewzenBusinessList] = useState<
    NewzenBusiness[]
  >([]);
  const [checkedBizCode, setCheckedBizCode] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertData>({
    message: "",
  });
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);

  useEffect(() => {
    if (bizId && isOpen) {
      setIsLoading(true);
      getNewzenBusinessList(bizId)
        .then((res) => {
          setIsLoading(false);
          const bizList = res.data;
          if (bizList) setNewzenBusinessList(bizList);
        })
        .catch((err) => {
          setIsLoading(false);
          const errMsg =
            err.response?.data?.message ||
            "세무사랑에 등록된 사업자 정보를 받아오는 중 오류가 발생했습니다.";
          setAlertData({
            message: errMsg,
            type: "error",
          });
          setIsAlertOpen(true);
        });
    }
  }, [bizId, isOpen]);

  const closeDialog = () => {
    onClose();
    setTimeout(() => {
      setNewzenBusinessList([]);
      setCheckedBizCode(null);
    }, 500);
  };

  const onSelectBiz = (e: React.ChangeEvent<HTMLInputElement>) => {
    const bizCode = e.target.value;
    if (checkedBizCode === bizCode) return;
    setCheckedBizCode(bizCode);
  };

  const sendReport = () => {
    if (!checkedBizCode) {
      setAlertData({
        message: "사업자를 선택해주세요.",
        type: "warning",
      });
      setIsAlertOpen(true);
      return;
    }

    if (!(spStartDate && spEndDate)) {
      setAlertData({
        message: "기간이 설정되지 않았습니다.",
        type: "warning",
      });
      setIsAlertOpen(true);
      return;
    }

    const data: UploadNewzenData = {
      bizCode: checkedBizCode,
      ids: selectedDataIds,
    };

    if (subType) data.invoiceType = subType;

    setIsUploadLoading(true);
    const newzenApi =
      reportType === "submit"
        ? uploadNewzenStore
        : reportType === "sync"
        ? syncNewzenData
        : null;

    if (!newzenApi) {
      setAlertData({
        message: "유효하지 않은 요청입니다. 다시 시도해주세요",
        type: "error",
      });
      setIsAlertOpen(true);
      setIsUploadLoading(false);
      return;
    }

    const messageData = {
      submit: {
        success: "전송이 완료되었습니다.",
        error: "전송 중 오류가 발생했습니다.",
      },
      sync: {
        success: "동기화가 완료되었습니다.",
        error: "동기화 중 오류가 발생했습니다.",
      },
    };

    newzenApi(bizId as string, data, storeType)
      .then((res) => {
        setIsUploadLoading(false);
        setAlertData({
          message: messageData[reportType].success,
          type: "success",
        });
        setIsAlertOpen(true);
        if (successCallback) successCallback();
        closeDialog();
      })
      .catch((err) => {
        setIsUploadLoading(false);
        const errMsg =
          err.response?.data?.message || messageData[reportType].error;
        setAlertData({
          message: errMsg,
          type: "error",
        });
        setIsAlertOpen(true);
      });
  };

  if (!bizId) return null;

  return (
    <>
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle
          css={css`
            background-color: ${PALETTE.background};
          `}
        >
          세무사랑 전자신고{" "}
          {reportType === "submit"
            ? "전송"
            : reportType === "sync"
            ? "동기화"
            : ""}
        </DialogTitle>
        <div
          css={css`
            height: 4px;
          `}
        >
          {isLoading && (
            <LinearProgress
              variant="indeterminate"
              color="primary"
              sx={{
                backgroundColor: PALETTE.primaryBackground,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: PALETTE.primary,
                },
              }}
            />
          )}
        </div>
        <DialogContent>
          <div
            css={css`
              min-width: 320px;
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            {newzenBusinessList.length > 0 ? (
              newzenBusinessList.map((item) => {
                return (
                  <div
                    key={item.code}
                    css={css`
                      padding: 16px;
                      border: 1px solid ${PALETTE.border};
                      border-radius: 8px;
                      display: flex;
                      align-items: center;
                      cursor: pointer;

                      ${checkedBizCode === item.code &&
                      `border-color: ${PALETTE.primary};`}

                      &:hover {
                        margin: -1px;
                        border-width: 2px;
                        border-color: ${PALETTE.primary};
                      }
                    `}
                  >
                    <BaseInput
                      id={item.code}
                      type="radio"
                      name={bizId}
                      value={item.code}
                      checked={checkedBizCode === item.code}
                      css={css`
                        &:checked {
                          accent-color: ${PALETTE.primary};
                        }
                      `}
                      onChange={onSelectBiz}
                    />
                    <label
                      htmlFor={item.code}
                      css={css`
                        width: 100%;
                        padding: 10px 10px 10px 20px;
                        cursor: pointer;
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                      `}
                    >
                      <div
                        css={css`
                          margin-bottom: 8px;
                          color: ${PALETTE.text};
                          font-weight: 600;
                        `}
                      >
                        {item.name}
                      </div>
                      <DisplayedInfo title="사업자코드" value={item.code} />
                      <DisplayedInfo
                        title="사업자번호"
                        value={item.regNo}
                        type="reg"
                      />
                      <DisplayedInfo
                        title="시작일"
                        value={item.startDate}
                        type="date"
                      />
                      <DisplayedInfo
                        title="종료일"
                        value={item.endDate}
                        type="date"
                      />
                      <DisplayedInfo title="기간" value={item.period} />
                    </label>
                  </div>
                );
              })
            ) : (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 200px;
                `}
              >
                {isLoading
                  ? "선택 가능한 사업자를 받아오는 중입니다."
                  : "선택 가능한 사업자가 없습니다."}
              </div>
            )}
          </div>
        </DialogContent>
        <div
          css={css`
            margin: 6px 24px;
            border-top: 1px solid ${PALETTE.border};
          `}
        />
        <DialogActions
          css={css`
            padding: 12px 24px 20px 24px;
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={closeDialog}
          >
            닫기
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            disabled={!checkedBizCode || isUploadLoading}
            onClick={sendReport}
            css={css`
              min-width: ${reportType === "submit" ? 80 : 90}px;
            `}
          >
            {reportType === "submit"
              ? isUploadLoading
                ? "전송 중"
                : "전송"
              : reportType === "sync"
              ? isUploadLoading
                ? "동기화 중"
                : "동기화"
              : ""}
          </BaseButton>
        </DialogActions>
      </Dialog>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertData}
      />
    </>
  );
}

const DisplayedInfo = ({
  title,
  value,
  type,
}: {
  title: string;
  value: string;
  type?: "reg" | "date";
}) => {
  let renderedValue = value;
  if (type === "reg") renderedValue = convertRegNumber(value);
  else if (type === "date")
    renderedValue = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(
      6,
      10
    )}`;

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${PALETTE.textMid};
      `}
    >
      <span
        css={css`
          font-size: 14px;
        `}
      >
        {title}
      </span>
      <span
        css={css`
          font-size: 15px;
        `}
      >
        {renderedValue}
      </span>
    </div>
  );
};

export default ReportDialog;
